import { getAvatarDomain } from './helper.js';
import { avatarHeaders } from 'a_common-folder/utils/avatar';

export default {
  currentController: null,

  async embedding(text) {
    if (this.currentController) {
      this.currentController.abort();
    }
    this.currentController = new AbortController();
    const res = await fetch(`https://${getAvatarDomain()}/embedding`, {
      method: "POST",
      headers: avatarHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ text }),
      signal: this.currentController.signal,
    });
    if (!res.ok) throw new Error("Failed to compute embedding");
    return await res.json();
  },
  async searchKnowledgeBase(query, json, top_k = 3) {
    if (this.currentController) {
      this.currentController.abort();
    }

    this.currentController = new AbortController();

    const source = json?.kbSelect || "";
    if(!source) {
      return [];
    }
    
    const res = await fetch(`https://${getAvatarDomain()}/search-knowledge-base`, {
      method: "POST",
      headers: avatarHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ source, query, top_k }),
      signal: this.currentController.signal,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`search-knowledge-base error: ${res.status} ${text}`);
    }

    const data = await res.json();
    return data?.results || [];
  },

  async searchIndex({ origin, index, text }) {
    if (this.currentController) {
      this.currentController.abort();
    }
    this.currentController = new AbortController();
    const res = await fetch(`https://${getAvatarDomain()}/search-index`, {
      method: "POST",
      headers: avatarHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ origin, index, text }),
      signal: this.currentController.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(`search-index error: ${res.status} ${msg}`);
    }
    return await res.json(); // expects { node_ids: [...] }
  },

  async searchRoom({ text, roomsData }) {
    if (this.currentController) {
      this.currentController.abort();
    }
    this.currentController = new AbortController();
    const res = await fetch(`https://${getAvatarDomain()}/search-room`, {
      method: "POST",
      headers: avatarHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ text, roomsData }),
      signal: this.currentController.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(`search-room error: ${res.status} ${msg}`);
    }
    return await res.json(); // expects { context: "..." }
  },

  // ── Parallel-safe variants (own AbortController, safe to call concurrently) ──

  async searchIndexParallel({ origin, index, text, signal = null }) {
    const ctrl = new AbortController();
    if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });
    const res = await fetch(`https://${getAvatarDomain()}/search-index`, {
      method: 'POST',
      headers: avatarHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ origin, index, text }),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => '');
      throw new Error(`search-index error: ${res.status} ${msg}`);
    }
    return res.json();
  },

  async searchRoomParallel({ text, roomsData, currentRoom = '', signal = null }) {
    const ctrl = new AbortController();
    if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });
    const res = await fetch(`https://${getAvatarDomain()}/search-room`, {
      method: 'POST',
      headers: avatarHeaders({ 'Content-Type': 'application/json' }),
      // currentRoom: tie-breaker when the question doesn't name a room
      body: JSON.stringify({ text, roomsData, ...(currentRoom ? { currentRoom } : {}) }),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => '');
      throw new Error(`search-room error: ${res.status} ${msg}`);
    }
    return res.json();
  },

  async searchKnowledgeBaseParallel(query, json, top_k = 3, signal = null) {
    const source = json?.kbSelect || '';
    if (!source) return [];
    const ctrl = new AbortController();
    if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });
    const res = await fetch(`https://${getAvatarDomain()}/search-knowledge-base`, {
      method: 'POST',
      headers: avatarHeaders({ 'Content-Type': 'application/json' }),
      // steering: hint for the backend relevance filter (tour type, current room)
      body: JSON.stringify({ source, query, top_k, ...(json?.steering ? { steering: json.steering } : {}) }),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => '');
      throw new Error(`search-knowledge-base error: ${res.status} ${msg}`);
    }
    const data = await res.json();
    return data?.results || [];
  },

  /**
   * Routes a user question to the relevant RAG source(s).
   * @param {{ question: string, sources: Array<{name: string, description: string}> }} param
   * @returns {Promise<string[]>} — names of sources to query (e.g. ["roomsData"])
   */
  async routeRagQuery({ question, sources }) {
    const ctrl = new AbortController();
    const res = await fetch(`https://${getAvatarDomain()}/route-rag-query`, {
      method: 'POST',
      headers: avatarHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ question, sources }),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => '');
      throw new Error(`route-rag-query error: ${res.status} ${msg}`);
    }
    const data = await res.json();
    return Array.isArray(data.sources) ? data.sources : [];
  },

  async originText(origin) {
    if (this.currentController) {
      this.currentController.abort();
    }
    this.currentController = new AbortController();
    const res = await fetch(`https://${getAvatarDomain()}/origin-text`, {
      method: "POST",
      headers: avatarHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ origin }),
      signal: this.currentController.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(`origin-text error: ${res.status} ${msg}`);
    }
    return await res.json(); // expects { pages: [...] }
  },

  async genLead(payload, urlOverride = "") {
    if (this.currentController) {
      this.currentController.abort();
    }
    this.currentController = new AbortController();
    const url = (urlOverride && urlOverride.trim())
      ? urlOverride.trim()
      : `https://${getAvatarDomain()}/gen-lead`;
    // lead_gen_url can point at a third-party CRM endpoint — only our own
    // backend gets the visitor id (and the CORS preflight it causes)
    const toAvatar = url.indexOf(`https://${getAvatarDomain()}/`) === 0;
    const jsonHeaders = { "Content-Type": "application/json" };
    const res = await fetch(url, {
      method: "POST",
      headers: toAvatar ? avatarHeaders(jsonHeaders) : jsonHeaders,
      body: JSON.stringify(payload),
      signal: this.currentController.signal,
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(`gen-lead error: ${res.status} ${msg}`);
    }
    return await res.json().catch(() => ({}));
  },

  async askGPT(text, json, conversation_history) {
      if (this.currentController) {
        this.currentController.abort();
      }

      this.currentController = new AbortController();

      const formData = new FormData();
      formData.append("text", text);
      formData.append("source", json.source  || "");
      formData.append("api", "gbt-server");
      formData.append("provider", json.provider);
      formData.append("model", json.ttsModel);
      formData.append("system_prompt", json.systemPrompt || "");
      formData.append("knowledge_base", json.kbSelect || "");
      formData.append("conversation_history", conversation_history || []);
      formData.append("id", json.id || null);
      formData.append("language_code", json.language_code || "en");

      const response = await fetch(`https://${getAvatarDomain()}/ask-server`, {
        method: "POST",
        headers: avatarHeaders(),
        body: formData, 
        signal: this.currentController.signal, 
      });

      const res = await response.json();

      return JSON.parse(res.message).metadata;
  },

  async transcribe(blob, json, conversation_history = [], errorCount = 0, onClose) {
    if (this.currentController) {
      this.currentController.abort();
    }

    this.currentController = new AbortController();

    try {
      const formData = new FormData();
      formData.append("file", blob, "speech.webm");
      formData.append("api", "transcribe");
      formData.append("source", json.source || "");
      formData.append("provider", json.provider);
      formData.append("model", json.ttsModel);
      formData.append("system_prompt", json.systemPrompt || "");
      formData.append("knowledge_base", json.kbSelect || "");
      formData.append("conversation_history", conversation_history || []);
      formData.append("id", json.id || null);
      formData.append("tourID", json.tourID || null);
      formData.append("language_code", json.language_code || null);

      const res = await fetch(`https://${getAvatarDomain()}/transcribe`, {
        method: "POST",
        headers: avatarHeaders(),
        body: formData,
        signal: this.currentController.signal,
      });

      if (!res.ok) {
        if (res.status === 422) {
          errorCount++;
          console.warn(`Transcription error 422 occurred ${errorCount} times`);
          if (errorCount > 10) {
            onClose();
            console.error("Error 422 occurred more than 10 times. Please check your input or server.");
          }
        }
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const contentType = res.headers.get("content-type") || "";
    
      if (contentType.includes("application/json")) {
        const data = await res.json();
        if (data.audio_path) {
          const audioRes = await fetch(data.audio_path);
          if (!audioRes.ok) throw new Error(`Failed to fetch audio: ${audioRes.status}`);
          return audioRes.body.getReader();
        } else {
          console.warn("JSON response received but no audio_path found:", data);
          return null;
        }
      } else {
        return res.body.getReader();
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn("Previous transcription request aborted.");
      } else {
        console.error("Transcription failed:", err);
      }
      return err;
    }
  },

  async tts(text, json, conversation_history = [], errorCount = 0, onClose) {
    if (this.currentController) {
      this.currentController.abort();
    }

    this.currentController = new AbortController();

    try {
      const formData = new FormData();
      formData.append("text", text);
      formData.append("api", "tts");
      formData.append("source", json.source || "");
      formData.append("provider", json.provider);
      formData.append("model", json.ttsModel);
      formData.append("system_prompt", json.systemPrompt || "");
      formData.append("knowledge_base", json.kbSelect || "");
      formData.append("conversation_history", conversation_history || []);
      formData.append("id", json.id || null);
      formData.append("tourID", json.tourID || null);
      formData.append("language_code", json.language_code || null);

      const res = await fetch(`https://${getAvatarDomain()}/tts`, {
        method: "POST",
        headers: avatarHeaders(),
        body: formData,
        signal: this.currentController.signal,
      });

      if (!res.ok) {
        if (res.status === 422) {
          errorCount++;
          console.warn(`Transcription error 422 occurred ${errorCount} times`);
          if (errorCount > 10) {
            onClose();
            console.error("Error 422 occurred more than 10 times. Please check your input or server.");
          }
        }
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const contentType = res.headers.get("content-type") || "";
    
      if (contentType.includes("application/json")) {
        const data = await res.json();
        if (data.audio_path) {
          const audioRes = await fetch(data.audio_path);
          if (!audioRes.ok) throw new Error(`Failed to fetch audio: ${audioRes.status}`);
          return audioRes.body.getReader();
        } else {
          console.warn("JSON response received but no audio_path found:", data);
          return null;
        }
      } else {
        return res.body.getReader();
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn("Previous transcription request aborted.");
      } else {
        console.error("Transcription failed:", err);
      }
      return err;
    }
  },

  async askGPTStream(text, json, conversation_history= [], language_code = "en-US") {
    if (this.currentController) {
      this.currentController.abort();
    }

    this.currentController = new AbortController();

    const formData = new FormData();
    formData.append("text", text);
    formData.append("source", json.source  || "");
    formData.append("api", "ask-server");
    formData.append("provider", json.provider);
    formData.append("model", json.ttsModel);
    formData.append("system_prompt", json.systemPrompt || "");
    formData.append("knowledge_base", json.kbSelect || "");
    formData.append("conversation_history", conversation_history || []);
    formData.append("language_code", language_code);
    formData.append("id", json.id || null);

    const response = await fetch(`https://${getAvatarDomain()}/ask-server`, {
      method: "POST",
      headers: avatarHeaders(),
      body: formData, 
      signal: this.currentController.signal, 
    });

    return response.body.getReader();
  },

  async newHeyGenSession(avatarId, quality = 'medium', sandbox = false, mode = 'FULL') {
    const resolvedMode = mode === 'LITE' ? 'LITE' : 'FULL';
    console.log(`[heygen][mode] requesting new-session with mode=${resolvedMode} (raw config value: ${JSON.stringify(mode)})`);
    const res = await fetch(`https://${getAvatarDomain()}/heygen/new-session`, {
      method: 'POST',
      headers: avatarHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ avatar_name: avatarId, quality, mode: resolvedMode, is_sandbox: sandbox }),
    });
    if (!res.ok) throw new Error(`LiveAvatar new-session error: ${res.status}`);
    const data = await res.json();
    return data.data;
  },

  async stopHeyGenSession(session_id, session_token) {
    const res = await fetch(`https://${getAvatarDomain()}/heygen/stop`, {
      method: 'POST',
      headers: avatarHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ session_id, session_token }),
    });
    return res.json();
  },

  async getHeyGenAvatars() {
    const res = await fetch(`https://${getAvatarDomain()}/heygen/avatars`, { headers: avatarHeaders() });
    if (!res.ok) throw new Error(`HeyGen avatars error: ${res.status}`);
    return res.json();
  },

  async getToken (model, voice, kb_key = '', tour_id = '', company_id = '') {
      const tokenRes = await fetch(`https://${getAvatarDomain()}/realtime-session`, {
        method: 'POST',
        headers: avatarHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ model, voice, kb_key, tour_id, company_id })
      });

      if (tokenRes.status === 403) {
        return { agent_disabled: true };
      }

      const session = await tokenRes.json();
      return session;
  },

   /**
   * Save conversation array to backend
   * @param {string} sessionId
   * @param {Array} conversationArray
   * @param {string} backendUrl - e.g. http://localhost:8000/conversation
   */
  async saveConversation(sessionId, conversationArray, userId = null, clientId = null, { keepalive = false } = {}) {
    try {
      const payload = {
        session_id: sessionId,
        conversation: conversationArray,
        ...(userId != null ? { user_id: userId } : {}),
        ...(clientId != null ? { client_id: clientId } : {}),
      };

      const url = `https://${getAvatarDomain()}/conversation`;

      const res = await fetch(url, {
        method: "POST",
        headers: avatarHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
        // pagehide flush: keepalive lets the request outlive the page
        // (sendBeacon can't carry the X-Visitor-Id header). Body limit 64 KB.
        ...(keepalive ? { keepalive: true } : {}),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to save conversation: ${res.status} ${text}`);
      }

      const data = await res.json().catch(() => ({}));
      console.log(`Conversation saved (${conversationArray.length} msg):`, data);
      return data;

    } catch (err) {
      console.error("saveConversation failed:", err);
      throw err;
    }
  },

  // Visitor behind a widget → tour handoff token (?esv=…); { user_id: null }
  // when the widget visitor was anonymous, null when the token is unknown/expired.
  async getTourVisitor(token) {
    const url = `https://${getAvatarDomain()}/api/widget/tour-visitor?token=${encodeURIComponent(token)}`;
    const res = await fetch(url, { headers: avatarHeaders() });
    if (!res.ok) return null;
    return await res.json().catch(() => null);
  },

  async getUserByPhone(phone) {
    const url = `https://${getAvatarDomain()}/user-by-phone?phone=${encodeURIComponent(phone)}`;
    const res = await fetch(url, { headers: avatarHeaders() });
    if (!res.ok) return null;
    return await res.json().catch(() => null);
  },
};
